import React, { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import RegistrationLinks from "../components/RegistrationLinks";
import Toast from "../components/Toast";
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';

import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Typography,
  Chip, Autocomplete,
  useTheme
} from "@mui/material";

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer, BarChart, Bar
} from "recharts";

import PageLoader from "../components/PageLoader";

const summaryData = [
  {
    title: "Total Users",
    value: "12,847",
    change: "+12% from last month",
    icon: "👥",
    column: 'totalUsers',
    url: "/admin/users"
  },
  {
    title: "Organizations",
    value: "2,341",
    change: "+8% from last month",
    icon: "🏢",
    column: 'totalOrganizations',
    url: "/admin/organizations"
  },
  {
    title: "Active Prayers",
    value: "8,492",
    change: "+23% from last month",
    icon: "🙏",
    column: 'approvedPrayers',
    url: "/admin/public-prayers",
    state: { status: '1' }
  },
  {
    title: "Answered Prayers",
    value: "0",
    change: "Praise reports",
    icon: "🙌",
    column: 'answeredPrayers',
    url: "/admin/testimonies"
  },
  {
    title: "Prayer Coverage",
    value: "100%",
    change: "Requests prayed for",
    icon: "🛡️",
    column: 'prayerCoverageRate',
    url: "/admin/prayers?tab=analytics"
  },
  {
    title: "Pending Approvals",
    value: "127",
    change: "Needs attention",
    icon: "⏰",
    column: 'pendingPrayers',
    url: "/admin/public-prayers",
    state: { status: '0' }
  },
];

const quickActions = [
  { label: "Add New Prayer", icon: "➕", url: "#" },
  { label: "Review Prayers", icon: "✔️", url: '/admin/public-prayers' },
  { label: "System Settings", icon: "⚙️", url: '/admin/settings' },
];


const Dashboard = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardCounts, setDashboardCounts] = useState([]);
  const [recentActivities1, setRecentActivities1] = useState(null);
  const [reportdata, setReportdata] = useState([]);
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [requestText, setRequestText] = useState("");
  const [title, setTitle] = useState("");
  const [formError, setFormError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [orgShortcode, setOrgShortcode] = useState('');
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [organizations, setOrganizations] = useState([]);
  const [formOrganizationId, setFormOrganizationId] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;


const handleDialogClose = () => {
    setOpenDialog(false);
    setFormError("");
    setFormOrganizationId(null);
  };

  const handleOrgLogoClick = () => {
    setOpenOrgDialog(true);
  };

  const handleOrgDialogClose = () => {
    setOpenOrgDialog(false);
  };

  const fetchProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const storedOrgMode = sessionStorage.getItem('gpn_org_view_mode');
      const parsedOrgMode = storedOrgMode ? JSON.parse(storedOrgMode) : null;
      const targetOrg = orgViewMode?.orgId || parsedOrgMode?.orgId || effectiveOrgId || (isSwitched || user?.role !== 1 ? user?.org_id : null);

      const url = targetOrg
        ? `${API_URL}dashboard-counts?org_id=${targetOrg}`
        : `${API_URL}dashboard-counts`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(targetOrg ? { 'x-org-id': String(targetOrg) } : {}),
        },
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        // 🔑 Handle invalid token
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login'; // redirect to login
          return;
        }

        setError(data.message || 'Failed to load profile');
      } else {
        setDashboardCounts(data.data.counts);
        setRecentActivities1(data.data.recent);
        setCategories(data.data.categories);
        setOrgShortcode(data.data.orgDetails?.short_code);

        const report = data.data.report;
        const formatted = Object.entries(report).map(([month, values]) => ({
          month,
          ...values,
        }));
        setReportdata(formatted);

         if (data.data.recent?.organisations) {
          setOrganizations(data.data.recent.organisations);
        }
      }

      // Fetch Feedback Count
      const feedbackUrl = targetOrg
        ? `${API_URL}roles-organizations?org_id=${targetOrg}`
        : `${API_URL}roles-organizations`;

      const resFeedback = await fetch(feedbackUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(targetOrg ? { 'x-org-id': String(targetOrg) } : {}),
        },
      });
      const dataFeedback = await resFeedback.json();
      if (resFeedback.ok) {
        setFeedbackCount(dataFeedback.allRating || 0);
      }

    } catch (err) {
      console.error(err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [effectiveOrgId, user?.org_id, orgViewMode?.orgId, isSwitched]);


  const filteredSummaryData = summaryData.filter(item => {
    if (item.column === 'totalOrganizations') {
      return user?.role === 1 && !isSwitched && !orgViewMode?.orgId;
    }
    return true; // Show all other cards
  });

  dayjs.extend(relativeTime);


  // After fetching your API data:
  const recentPrayers = recentActivities1?.recentPrayers || [];
  const recentTestimonies = recentActivities1?.recentTestimonies || [];
  const recentSpecialPrayerSubscriptions = recentActivities1?.recentSpecialPrayerSubscriptions || [];

  const recentActivities = [
    ...recentPrayers.map(item => ({
      user: `${item.app_users.first_name} ${item.app_users.last_name}`,
      action: `submitted a prayer request: "${item.title}"`,
      time: dayjs(item.created_at).fromNow(), // or use your own formatting
      type: "Prayer",
    })),
    ...recentTestimonies.map(item => ({
      user: `${item.app_users.first_name} ${item.app_users.last_name}`,
      action: `shared a testimony: "${item.title}"`,
      time: dayjs(item.created_at).fromNow(),
      type: "Testimony",
    })),
    ...recentSpecialPrayerSubscriptions.map(item => ({
      user: `${item.app_users.first_name} ${item.app_users.last_name}`,
      action: `subscribed to special prayer: "${item.session_prayers.title}"`,
      time: dayjs(item.created_at).fromNow(),
      type: "Special Subscription",
    })),
  ].sort((a, b) => new Date(b.time) - new Date(a.time)); // Optional: sort by time

  const quicklinks = (url) => {
    if (url == "#") {
      setOpenDialog(true)
    } else {
      navigate(url);
    }

  }

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !title.trim() || !requestText.trim()) {
      setFormError("Please select a category and title and enter your prayer request.");
      return;
    }
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}add-public-prayer`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category: category,
        title: title,
        description: requestText,
        priority: priority,
        organization_id: formOrganizationId?.id || null,
      }),
    });
    const data = await response.json();
    if (data.status === 200) {
      setToast({ open: true, message: "Prayer added successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      setToast({ open: true, message: "Prayer added successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }


  };

  if (loading) {
    return (
      <div>
        {/* <button onClick={fetchData}>Load Data</button> */}
        <PageLoader open={loading} />
      </div>
    );
  }




  return (
    <>
      <Helmet>
        <title>Dashboard - Glocal Prayer Network</title>
      </Helmet>

      {/* Modern Background Container */}
      <Box sx={{
        p: { xs: 1.5, sm: 3 },
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        minHeight: '100vh',
        background: isLight ? 'transparent' : 'linear-gradient(135deg, #1a1f2e 0%, #2C3E50 100%)',
      }}>
        {/* Header Section */}
        <Box sx={{
          mb: 4,
          pb: 3,
          pt: 2.5,
          px: 3,
          background: isLight
            ? '#FFFFFF'
            : 'linear-gradient(135deg, rgba(76, 161, 175, 0.15) 0%, rgba(44, 62, 80, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.3)',
          boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : '0 4px 12px rgba(76, 161, 175, 0.2)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{
                fontWeight: 800,
                color: isLight ? '#0F172A' : '#FFFFFF',
                mb: 0.5,
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}>
                Dashboard Overview
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                Real-time insights and analytics for your network
              </Typography>
            </Box>
            <Chip
              label="Live"
              size="small"
              sx={{
                background: isLight ? '#ECFDF5' : 'rgba(16, 185, 129, 0.2)',
                color: isLight ? '#059669' : '#10B981',
                fontWeight: 700,
                border: isLight ? '1.5px solid #A7F3D0' : '1px solid rgba(16, 185, 129, 0.3)',
              }}
            />
          </Box>
        </Box>

        {/* Error Alert State */}
        {error && (
          <Box sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: isLight ? '#FEF2F2' : 'rgba(239, 68, 68, 0.15)',
            border: '1.5px solid #EF4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
          }}>
            <Typography variant="body2" sx={{ color: isLight ? '#991B1B' : '#FCA5A5', fontWeight: 600 }}>
              {error}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={fetchProfile}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700 }}
            >
              Retry Loading
            </Button>
          </Box>
        )}

        {/* Ministry Impact & Prayer Consistency Banner */}
        <Paper elevation={0} sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 4,
          borderRadius: 2.5,
          background: isLight 
            ? 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 50%, #F0FDFA 100%)' 
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(13, 148, 136, 0.15) 100%)',
          backdropFilter: 'blur(12px)',
          border: isLight ? '1.5px solid #A7F3D0' : '1px solid rgba(16, 185, 129, 0.3)',
          boxShadow: isLight ? '0 4px 14px rgba(16, 185, 129, 0.08)' : '0 4px 16px rgba(16, 185, 129, 0.15)',
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isLight ? '#D1FAE5' : 'rgba(16, 185, 129, 0.25)',
                fontSize: 24
              }}>
                🙏
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: isLight ? '#065F46' : '#6EE7B7',
                  fontSize: '0.8rem'
                }}>
                  Ministry Impact & Prayer Consistency
                </Typography>
                <Typography variant="body2" sx={{
                  color: isLight ? '#047857' : 'rgba(255,255,255,0.7)',
                  fontSize: '0.85rem'
                }}>
                  Core habit formation and spiritual outcomes across the body of Christ
                </Typography>
              </Box>
            </Box>
            <Button
              component={Link}
              to="/admin/prayers?tab=analytics"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.825rem',
                color: isLight ? '#047857' : '#A7F3D0',
                background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
                border: isLight ? '1px solid #A7F3D0' : '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 2,
                px: 2,
                py: 0.8,
                '&:hover': {
                  background: isLight ? '#E6FFFA' : 'rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              Believer Activity Analytics →
            </Button>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.2fr 1fr 1fr 1fr' },
            gap: 2.5,
            pt: 2,
            borderTop: isLight ? '1px solid rgba(5, 150, 105, 0.15)' : '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Box>
              <Typography variant="caption" sx={{
                color: isLight ? '#065F46' : 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                display: 'block',
                mb: 0.5
              }}>
                🌟 North-Star: Weekly Intercessors
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 900,
                color: isLight ? '#064E3B' : '#FFFFFF',
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}>
                {dashboardCounts?.weeklyActiveIntercessors ?? 0}
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#047857' : '#6EE7B7', fontWeight: 600 }}>
                Believers praying for others this week
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{
                color: isLight ? '#065F46' : 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                display: 'block',
                mb: 0.5
              }}>
                🛡️ Prayer Coverage Rate
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 900,
                color: isLight ? '#064E3B' : '#FFFFFF',
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}>
                {dashboardCounts?.prayerCoverageRate ?? 100}%
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#047857' : '#6EE7B7', fontWeight: 600 }}>
                Requests receiving at least 1 prayer
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{
                color: isLight ? '#065F46' : 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                display: 'block',
                mb: 0.5
              }}>
                🙌 Answered Prayers & Praises
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 900,
                color: isLight ? '#064E3B' : '#FFFFFF',
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}>
                {dashboardCounts?.answeredPrayers ?? 0}
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#047857' : '#6EE7B7', fontWeight: 600 }}>
                Recorded praise reports & testimonies
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{
                color: isLight ? '#065F46' : 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                display: 'block',
                mb: 0.5
              }}>
                ❤️ Total Prayers & Amens
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 900,
                color: isLight ? '#064E3B' : '#FFFFFF',
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}>
                {dashboardCounts?.totalIntercessions ?? 0}
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#047857' : '#6EE7B7', fontWeight: 600 }}>
                Intercessions lifted network-wide
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Stats Grid - Responsive Grid with Equal Heights */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(auto-fit, minmax(200px, 1fr))'
          },
          gap: 2.5,
          mb: 4,
          width: '100%'
        }}>
          {filteredSummaryData.map((item, index) => {
            const cardColors = ['#0D9488', '#8B5CF6', '#10B981', '#3B82F6', '#6366F1', '#F59E0B', '#EC4899'];
            const accentColor = cardColors[index % cardColors.length];
            return (
              <Paper
                key={item.title}
                component={Link}
                to={item.url}
                state={item.state}
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: isLight ? '0 2px 6px rgba(15, 23, 42, 0.05)' : 'none',
                  textDecoration: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 140,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: accentColor,
                  },
                  '&:hover': {
                    background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
                    borderColor: accentColor,
                    transform: 'translateY(-4px)',
                    boxShadow: isLight
                      ? '0 12px 24px rgba(15, 23, 42, 0.08)'
                      : '0 12px 24px rgba(0, 0, 0, 0.35)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="caption" sx={{
                    color: isLight ? '#1E293B' : 'rgba(255,255,255,0.85)',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {item.title}
                  </Typography>
                  <Box sx={{
                    fontSize: 22,
                    opacity: isLight ? 0.9 : 0.6,
                    filter: isLight ? 'none' : 'grayscale(100%) brightness(2)',
                  }}>
                    {item.icon}
                  </Box>
                </Box>

                <Typography variant="h4" sx={{
                  fontWeight: 800,
                  color: isLight ? '#0F172A' : '#FFFFFF',
                  mb: 1,
                  fontSize: '1.85rem',
                }}>
                  {item.column === 'prayerCoverageRate'
                    ? `${dashboardCounts?.prayerCoverageRate ?? 100}%`
                    : (dashboardCounts?.[item.column] ?? 0)}
                </Typography>

                <Typography variant="caption" sx={{
                  color: '#059669',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  mt: 'auto',
                }}>
                  {item.change}
                </Typography>
              </Paper>
            );
          })}

          {/* Feedbacks Card */}
          <Paper
            component={Link}
            to="/admin/feedback"
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
              boxShadow: isLight ? '0 2px 6px rgba(15, 23, 42, 0.05)' : 'none',
              textDecoration: 'none',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 140,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: '#EC4899',
              },
              '&:hover': {
                background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
                borderColor: '#EC4899',
                transform: 'translateY(-4px)',
                boxShadow: isLight ? '0 12px 24px rgba(236, 72, 153, 0.15)' : '0 12px 24px rgba(236, 72, 153, 0.3)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="caption" sx={{
                color: isLight ? '#1E293B' : 'rgba(255,255,255,0.85)',
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                TOTAL FEEDBACKS
              </Typography>
              <Box sx={{
                fontSize: 22,
                opacity: isLight ? 0.9 : 0.6,
                filter: isLight ? 'none' : 'grayscale(100%) brightness(2)',
              }}>
                💬
              </Box>
            </Box>

            <Typography variant="h4" sx={{
              fontWeight: 800,
              color: isLight ? '#0F172A' : '#FFFFFF',
              mb: 1,
              fontSize: '1.85rem',
            }}>
              {feedbackCount}
            </Typography>

            <Typography variant="caption" sx={{
              color: '#059669',
              fontWeight: 700,
              fontSize: '0.8rem',
              mt: 'auto',
            }}>
              Total received
            </Typography>
          </Paper>
        </Box>

        {/* Main Content Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2.5fr 1fr' },
          gap: 3,
          mb: 4,
          width: '100%'
        }}>
          {/* Chart Section */}
          <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : 'none',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: isLight ? '#0F172A' : '#FFFFFF',
                fontSize: '1.125rem',
                mb: 0.5,
              }}>
                Growth Analytics
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }}>
                {user?.role === 1 ? "Organizations" : "Users"} growth over the last 5 months
              </Typography>
            </Box>

            {reportdata && reportdata.length > 0 ? (
              <Box sx={{ width: "100%", height: 320, flex: 1 }}>
                <ResponsiveContainer>
                  <BarChart data={reportdata} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                    <defs>
                      <linearGradient id="modernBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={isLight ? "#0D9488" : "#4CA1AF"} stopOpacity={1} />
                        <stop offset="100%" stopColor={isLight ? "#14B8A6" : "#4CA1AF"} stopOpacity={isLight ? 0.35 : 0.3} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isLight ? '#CBD5E1' : 'rgba(255,255,255,0.1)'} vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: isLight ? '#0F172A' : 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600 }}
                      axisLine={{ stroke: isLight ? '#94A3B8' : 'rgba(255,255,255,0.2)' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: isLight ? '#0F172A' : 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600 }}
                      axisLine={{ stroke: isLight ? '#94A3B8' : 'rgba(255,255,255,0.2)' }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: isLight ? '#0F172A' : '#FFFFFF',
                        boxShadow: isLight ? '0 4px 12px rgba(15,23,42,0.08)' : 'none',
                        padding: '8px 12px',
                      }}
                      cursor={{ fill: isLight ? 'rgba(13, 148, 136, 0.08)' : 'rgba(76, 161, 175, 0.1)' }}
                    />
                    {user?.role === 1 ? (
                      <Bar
                        dataKey="organizations"
                        fill="url(#modernBar)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={45}
                      />
                    ) : (
                      <Bar
                        dataKey="users"
                        fill="url(#modernBar)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={45}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  No growth trends recorded for this period yet.
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Quick Actions */}
          <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : 'none',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              color: isLight ? '#0F172A' : '#FFFFFF',
              mb: 3,
              fontSize: '1.125rem',
            }}>
              Quick Actions
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
              {quickActions.map((action, idx) => (
                <Button
                  key={action.label}
                  onClick={() => quicklinks(action.url)}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 2,
                    px: 2.5,
                    background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.05)',
                    border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
                    color: isLight ? '#0F172A' : '#FFFFFF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isLight
                        ? '#F0FDFA'
                        : idx === 0 ? 'rgba(76, 161, 175, 0.2)' : idx === 1 ? 'rgba(139, 92, 246, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      borderColor: idx === 0 ? (isLight ? '#0D9488' : '#4CA1AF') : idx === 1 ? '#8B5CF6' : '#10B981',
                      transform: 'translateX(4px)',
                    },
                  }}
                  startIcon={<span style={{ fontSize: 20 }}>{action.icon}</span>}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Registration Links */}
        {user?.role !== 1 && (
          <Box sx={{ mb: 4 }}>
            <RegistrationLinks shortCode={orgShortcode} />
          </Box>
        )}

        {/* Recent Activity */}
        <Paper elevation={0} sx={{
          p: 3,
          borderRadius: 2,
          background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : 'none',
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: isLight ? '#0F172A' : '#FFFFFF',
            mb: 3,
            fontSize: '1.125rem',
          }}>
            Recent Activity
          </Typography>

          <Box>
            {recentActivities && recentActivities.length > 0 ? (
              recentActivities.slice(0, 8).map((activity, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 2,
                    px: 2,
                    borderRadius: 1.5,
                    borderBottom: idx < Math.min(recentActivities.length, 8) - 1
                      ? (isLight ? "1.5px solid #E2E8F0" : "1px solid rgba(255,255,255,0.05)")
                      : "none",
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.2)',
                    border: isLight ? '1.5px solid #99F6E4' : '2px solid rgba(76, 161, 175, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    flexShrink: 0,
                  }}>
                    👤
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{
                      color: isLight ? '#0F172A' : '#FFFFFF',
                      fontWeight: 500,
                      mb: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      <strong style={{ fontWeight: 700 }}>{activity.user}</strong> {activity.action}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                      {activity.time}
                    </Typography>
                  </Box>

                  <Chip
                    label={activity.type}
                    size="small"
                    sx={{
                      background: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.3)',
                      color: isLight ? '#0D9488' : '#FFFFFF',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 26,
                      borderRadius: 1,
                      border: isLight ? '1.5px solid #5EEAD4' : '1px solid rgba(76, 161, 175, 0.5)',
                      display: { xs: 'none', sm: 'flex' },
                    }}
                  />
                </Box>
              ))
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  No recent activities recorded yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Submit Prayer Request Modal */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.98)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2.5,
            color: isLight ? '#0F172A' : '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF' }}>Submit Prayer Request</DialogTitle>
        <DialogContent>
          <form id="prayer-form" onSubmit={handleSubmit}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"   // 👈 link to InputLabel
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
                data-testid="category-select"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            {user?.role === 1 && (
              <Autocomplete
                size="small"
                value={formOrganizationId}
                onChange={(event, newValue) => {
                  setFormOrganizationId(newValue);
                }}
                options={organizations}
                getOptionLabel={(option) => option?.org_name || ""}
                renderInput={(params) => <TextField {...params} label="Organization" margin="dense" />}
                sx={{ width: '100%', mt: 1 }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}


            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value)}
                data-testid="priority-select"
              >
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Title"
              fullWidth
              margin="dense"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="title-input"
            />


            <TextField
              label="Prayer Request"
              multiline
              rows={4}
              fullWidth
              margin="dense"
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              error={!!formError}
              helperText={formError}
              data-testid="request-input"
            />


          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, borderTop: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Button 
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.2)',
              color: isLight ? '#475569' : '#FFFFFF',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: isLight ? '#94A3B8' : 'rgba(255, 255, 255, 0.4)',
                background: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            form="prayer-form" 
            variant="contained" 
            sx={{
              background: isLight ? 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' : undefined,
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              px: 3,
              boxShadow: isLight ? '0 2px 6px rgba(13, 148, 136, 0.3)' : undefined,
              '&:hover': {
                background: isLight ? 'linear-gradient(135deg, #0F766E 0%, #115E59 100%)' : undefined,
              }
            }}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      <Toast toast={toast} setToast={setToast} />
    </>
  );
};

export default Dashboard;